import { action as sharingSettingsAction } from "@components/Communicate/SharingSettings";
import { loader as actionDataLoader } from "@components/Onboarding/OnboardingActionsSection";
import { loader as resourceDataLoader } from "@components/Onboarding/OnboardingResourcesSection";
import { loader as allResourcesLoader } from "@components/Onboarding/ViewAllResourcesModal";
import Loader from "@components/ui/Loader";
import { createBrowserRouter } from "react-router";

import Communicate, {
  action as communicateAction,
  loader as communicateLoader,
} from "@/pages/Communicate";
import CreateTrackingCampaign, {
  action as createTrackingCampaignAction,
  loader as createTrackingCampaignLoader,
} from "@/pages/CreateTrackingCampaign";
import Dashboard, { loader as dashboardLoader } from "@/pages/Dashboard";
import CreateExternalCampaign, {
  action as createExternalCampaignAction,
  loader as createExternalCampaignLoader,
} from "@/pages/ExternalCampaign/CreateExternalCampaign";
import ScopesExternalCampaign, {
  action as scopesExternalCampaignAction,
  loader as scopesExternalCampaignLoader,
} from "@/pages/ExternalCampaign/ScopesExternalCampaign";
import ImportDeposit, {
  action as importDepositAction,
  loader as importDepositLoader,
} from "@/pages/ImportDeposit";
import Onboarding, {
  action as onboardingAction,
  loader as onboardingLoader,
} from "@/pages/Onboarding";
import PasswordConstraints from "@/pages/PasswordConstraints";
import Pricing from "@/pages/Pricing";
import Review, { loader as reviewLoader } from "@/pages/Review";
import Root, { action as rootAction, loader as rootLoader } from "@/pages/Root";
import Simulation, { loader as simulationLoader } from "@/pages/Simulation";
import SimulationCategories, {
  action as simulationCategoriesAction,
  loader as simulationCategoriesLoader,
} from "@/pages/SimulationCategories";
import SimulationParameters, {
  action as simulationParametersAction,
} from "@/pages/SimulationParameters";
import Simulations, {
  action as simulationsAction,
  loader as simulationsLoader,
} from "@/pages/Simulations";
import SimulationScenarios, {
  action as simulationScenariosAction,
  loader as simulationScenariosLoader,
} from "@/pages/SimulationScenarios";
import {
  action as trajectoryActionPlanAction,
  loader as trajectoryActionPlanLoader,
} from "@/pages/Trajectory/TrajectoryActionPlan";
import {
  action as trajectoryProjectionsAction,
  loader as trajectoryProjectionsLoader,
} from "@/pages/Trajectory/TrajectoryProjections";
import { loader as trajectorySummaryLoader } from "@/pages/Trajectory/TrajectorySummary";

import { NavigationMenuLinks } from "./components/NavigationMenu";
import App, { loader as appLoader } from "./pages/App";
import Collect, { loader as collectLoader } from "./pages/Collect";
import ErrorBoundary from "./pages/ErrorBoundary";
import Feedback, { handle as reviewFeedbackHandle } from "./pages/Feedback";
import ImportDepositDetail, {
  loader as importDepositDetailLoader,
} from "./pages/ImportDepositDetail";
import ImportDepositMatching, {
  action as importDepositMatchingAction,
  loader as importDepositMatchingLoader,
} from "./pages/ImportDepositMatching";
import ImportDepositSummary, {
  loader as importDepositSummaryLoader,
} from "./pages/ImportDepositSummary";
import Options, {
  handle as optionsHandle,
  loader as optionsLoader,
} from "./pages/Options";
import OptionsIndex from "./pages/OptionsIndex";
import ReviewComparison, {
  handle as reviewComparisonHandle,
} from "./pages/ReviewComparison";
import ReviewExports, {
  handle as reviewExportsHandle,
  loader as reviewExportsLoader,
} from "./pages/ReviewExports";
import ReviewIndex, { handle as reviewIndexHandle } from "./pages/ReviewIndex";
import SignUp, {
  action as signUpAction,
  loader as signUpLoader,
} from "./pages/SignUp";
import SubscriptionOptions, {
  handle as subscriptionOptionsHandle,
} from "./pages/SubscriptionOptions";
import SuccessExternalCampaign from "./pages/SuccessExternalCampaign";
import {
  Trajectory,
  TrajectoryActionPlan,
  TrajectoryBuildActionPlan,
  TrajectoryContext,
  TrajectoryCreation,
  TrajectoryIntroduction,
  TrajectoryProjections,
  TrajectorySummary,
} from "./pages/Trajectory";
import {
  collectCategoryHandle,
  collectEmployeeSurveyHandle,
  collectGeneralInfoHandle,
  collectHandle,
  collectImportedDepositsHandle,
  collectImportMatchingHandle,
  collectImportSummaryHandle,
  collectSubCategoryHandle,
  collectSurveyCreateHandle,
  collectSurveyHandle,
  publicComparisonReviewIndexHandle,
  publicReviewIndexHandle,
  publicReviewSubCategoryHandle,
  reviewSubCategoryHandle,
  trialHandle,
} from "./utils/railsPagesHandles";

const router = createBrowserRouter([
  {
    path: ":lang?",
    element: <Root />, // Parent route to _all_ pages
    loader: rootLoader,
    action: rootAction,
    ErrorBoundary: ErrorBoundary, // here user is always undefined
    HydrateFallback: Loader,
    children: [
      {
        index: true,
        element: <SignUp />,
        loader: signUpLoader,
        action: signUpAction,
      },
      {
        path: "users",
        children: [
          {
            path: "sign_up",
            element: <SignUp />,
            loader: signUpLoader,
            action: signUpAction,
          },
          {
            path: "sign_in",
            element: null,
          },
          {
            path: "invitation/accept",
            element: <PasswordConstraints />,
          },
          {
            path: "password/edit",
            element: <PasswordConstraints />,
          },
          {
            path: ":userId/phone_number",
            element: null,
          },
        ],
      },
      {
        element: <App />, // Parent route to logged-in pages
        loader: appLoader,
        children: [
          {
            path: "*", // child of App so that ErrorBoundary can use UserContext
            ErrorBoundary: ErrorBoundary,
            element: null,
            children: [
              {
                path: "trajectory/creation",
                element: <TrajectoryCreation />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.trajectory,
                },
                children: [
                  {
                    path: "introduction",
                    element: <TrajectoryIntroduction />,
                  },
                  {
                    path: "context",
                    element: <TrajectoryContext />,
                  },
                  {
                    path: "projections",
                    element: <TrajectoryProjections />,
                    loader: trajectoryProjectionsLoader,
                    action: trajectoryProjectionsAction,
                  },
                  {
                    path: "summary",
                    element: <TrajectorySummary />,
                    loader: trajectorySummaryLoader,
                  },
                  {
                    path: "build-action-plan",
                    element: <TrajectoryBuildActionPlan />,
                  },
                ],
              },
              {
                path: "trajectory",
                element: <Trajectory />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.trajectory,
                },
                children: [
                  {
                    path: "action-plan",
                    element: <TrajectoryActionPlan />,
                    loader: trajectoryActionPlanLoader,
                    action: trajectoryActionPlanAction,
                  },
                  {
                    path: "projections",
                    element: <TrajectoryProjections />,
                    loader: trajectoryProjectionsLoader,
                    action: trajectoryProjectionsAction,
                  },
                  { path: "context", element: <TrajectoryContext /> },
                ],
              },
              {
                path: "onboarding",
                loader: onboardingLoader,
                action: onboardingAction,
                element: <Onboarding />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.index,
                },
                children: [
                  {
                    path: "all-resources",
                    loader: allResourcesLoader,
                  },
                  {
                    path: "action-data",
                    loader: actionDataLoader,
                  },
                  {
                    path: "resource-data",
                    loader: resourceDataLoader,
                  },
                ],
              },
              {
                path: "dashboard/consolidation",
                element: <Dashboard showComparison={false} />,
                loader: dashboardLoader,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.index,
                },
              },
              {
                path: "dashboard",
                element: <Dashboard showComparison />,
                loader: dashboardLoader,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.index,
                },
              },
              {
                path: "reviews/communicate/:reviewId",
                element: <Communicate />,
                loader: communicateLoader,
                action: communicateAction,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.communicate,
                },
                children: [
                  {
                    path: "sharing_settings",
                    action: sharingSettingsAction,
                  },
                ],
              },
              {
                path: "reviews/pending",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews/cancel",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews/canceled",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews",
                element: <Review />,
                handle: reviewIndexHandle,
                loader: reviewLoader,
                children: [
                  {
                    path: ":reviewId/:admin?",
                    element: null,
                    children: [
                      {
                        index: true,
                        element: <ReviewIndex />,
                      },
                      {
                        path: "feedback",
                        element: <Feedback />,
                        handle: reviewFeedbackHandle,
                      },
                      {
                        path: "exports",
                        element: <ReviewExports />,
                        loader: reviewExportsLoader,
                        handle: reviewExportsHandle,
                      },
                      {
                        path: "comparison/:admin?",
                        element: <ReviewComparison />,
                        handle: reviewComparisonHandle,
                      },
                      {
                        path: "review_categories/:reviewCategoryId/:admin?",
                        handle: reviewSubCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                ],
              },
              {
                path: "reviews/:slug/public",
                element: <Review />,
                loader: reviewLoader,
                handle: publicReviewIndexHandle,
                children: [
                  {
                    index: true,
                    element: <ReviewIndex />,
                  },
                  {
                    path: "comparison",
                    element: <ReviewComparison />,
                    handle: publicComparisonReviewIndexHandle,
                  },
                  {
                    path: ":reviewCategoryId",
                    handle: publicReviewSubCategoryHandle,
                    element: null, // We render nothing to let the rails page show
                  },
                ],
              },
              {
                path: "simulations",
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.simulate,
                },
                children: [
                  {
                    index: true,
                    element: <Simulations />,
                    loader: simulationsLoader,
                    action: simulationsAction,
                  },
                  {
                    path: "scenarios",
                    element: <SimulationScenarios />,
                    loader: simulationScenariosLoader,
                    action: simulationScenariosAction,
                  },
                  {
                    path: ":simulationId/finalize",
                    element: null,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.simulate,
                    },
                  },
                  {
                    path: ":simulationId",
                    element: <Simulation />,
                    loader: simulationLoader,
                    children: [
                      {
                        path: "parameters",
                        element: <SimulationParameters />,
                        action: simulationParametersAction,
                      },
                      {
                        path: "categories",
                        element: <SimulationCategories />,
                        loader: simulationCategoriesLoader,
                        action: simulationCategoriesAction,
                      },
                    ],
                  },
                ],
              },
              {
                path: "tracking",
                children: [
                  {
                    path: "campaigns/new",
                    element: <CreateTrackingCampaign />,
                    loader: createTrackingCampaignLoader,
                    action: createTrackingCampaignAction,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.collect,
                    },
                  },
                  {
                    path: "external_campaigns",
                    children: [
                      {
                        path: "new",
                        element: <CreateExternalCampaign />,
                        loader: createExternalCampaignLoader,
                        action: createExternalCampaignAction,
                        handle: {
                          activeMenuLink: () => NavigationMenuLinks.collect,
                        },
                      },
                      {
                        path: ":externalCampaignId/scopes",
                        element: <ScopesExternalCampaign />,
                        loader: scopesExternalCampaignLoader,
                        action: scopesExternalCampaignAction,
                        handle: {
                          activeMenuLink: () => NavigationMenuLinks.collect,
                        },
                      },
                      {
                        path: ":externalCampaignId/success",
                        element: <SuccessExternalCampaign />,
                        handle: {
                          activeMenuLink: () => NavigationMenuLinks.collect,
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: "trial",
                handle: trialHandle,
                element: <Collect />,
                loader: collectLoader,
              },
              {
                path: "categories",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
              },
              {
                path: "tracking/campaigns/:campaignId",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
                children: [
                  {
                    handle: collectGeneralInfoHandle,
                    path: "edit",
                    element: null, // We render nothing to let the rails page show
                  },
                ],
              },
              {
                path: "users",
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.users,
                },
                children: [
                  { index: true, element: null },
                  {
                    path: "edit",
                    element: null,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.myAccount,
                    },
                  },
                  {
                    path: "sign_in",
                    element: null,
                  },
                  { path: "*", element: <PasswordConstraints /> },
                ],
              },
              {
                path: "pricing",
                element: <Pricing />,
              },
              {
                path: "options",
                element: <OptionsIndex />,
                handle: optionsHandle,
                children: [
                  {
                    index: true,
                    element: <Options />,
                    loader: optionsLoader,
                  },
                  {
                    path: "subscription",
                    element: <SubscriptionOptions />,
                    handle: subscriptionOptionsHandle,
                  },
                ],
              },
              {
                path: "scores/:scoreId/",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
                children: [
                  {
                    path: "categories",
                    element: null, // We render nothing to let the rails page show
                    children: [
                      {
                        path: ":categoryId",
                        handle: collectCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                  {
                    path: "survey/team/report",
                    handle: collectEmployeeSurveyHandle,
                    element: null, // We render nothing to let the rails page show
                  },
                  {
                    path: "survey",
                    handle: collectSurveyHandle,
                    element: null, // We render nothing to let the rails page show
                    children: [
                      {
                        path: ":subCategory/*",
                        handle: collectSubCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                  {
                    path: "import_deposits",
                    children: [
                      {
                        index: true,
                        handle: collectImportedDepositsHandle,
                        element: <ImportDeposit />, // We let the element render null if the migration Feature-Flag is off
                        loader: importDepositLoader,
                        action: importDepositAction,
                      },
                      {
                        path: "new",
                        handle: collectImportedDepositsHandle, // TO DO add a proper handle
                        element: null,
                      },
                      {
                        path: ":importId",
                        handle: collectImportSummaryHandle,
                        element: <ImportDepositDetail />,
                        loader: importDepositDetailLoader,
                      },
                      {
                        path: ":importId/matching",
                        loader: importDepositSummaryLoader,
                        handle: collectImportMatchingHandle,
                        element: <ImportDepositSummary />, // This is an empty parent route, to load resources that never need re-load for the children
                        shouldRevalidate: () => false,
                        children: [
                          {
                            index: true,
                            element: <ImportDepositMatching />,
                            loader: importDepositMatchingLoader,
                            action: importDepositMatchingAction,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "survey/:subCategory/*",
                element: <Collect />, // We render nothing to let the rails page show
                loader: collectLoader,
                handle: collectSurveyCreateHandle,
              },
              {
                path: "subsidiary-choice",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.switchEntity,
                },
              },
              {
                path: "companies/*",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.myCompany,
                },
              },
              {
                path: "trial_result",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "*",
                element: null, // We render nothing to let the rails page show
              },
            ],
          },
        ],
      },
    ],
  },
]);
export default router;
